import { useParams, Link } from "react-router-dom";
import SEOHead from "@/components/feature/SEOHead";
import { ArticleSchema, WebPageSchema } from "@/components/feature/SchemaOrg";
import Breadcrumbs from "@/components/feature/Breadcrumbs";
import ArticleCard from "@/components/base/ArticleCard";
import { articles } from "@/mocks/articles";

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const article = articles.find((a) => a.slug === slug);

  if (!article) {
    return (
      <main className="pt-20">
        <section className="w-full min-h-[60vh] flex flex-col items-center justify-center px-4">
          <div className="w-16 h-16 mb-4 flex items-center justify-center bg-background-100 rounded-full">
            <i className="ri-error-warning-line text-2xl text-foreground-400"></i>
          </div>
          <h1 className="text-xl font-heading font-bold text-foreground-950 mb-2">文章未找到</h1>
          <p className="text-sm text-foreground-600 mb-6">抱歉，该评测文章不存在或已被移除。</p>
          <Link
            to="/"
            className="px-5 py-2.5 bg-primary-500 text-white text-sm font-medium rounded-md hover:bg-primary-600 transition-colors cursor-pointer"
          >
            返回首页
          </Link>
        </section>
      </main>
    );
  }

  const relatedArticles = articles
    .filter((a) => a.id !== article.id && a.category === article.category)
    .slice(0, 3);

  const canonicalUrl = `https://mogutv.com/article/${article.slug}`;

  return (
    <>
      <SEOHead
        title={`${article.title} - 蘑菇视频评测`}
        description={article.excerpt}
        keywords={article.tags.join(",")}
        canonicalUrl={canonicalUrl}
        ogType="article"
      />
      <ArticleSchema
        title={article.title}
        description={article.excerpt}
        url={canonicalUrl}
        author={article.author}
        publishDate={article.publishDate}
        imageUrl={article.coverImage}
      />
      <WebPageSchema
        title={article.title}
        description={article.excerpt}
        url={canonicalUrl}
        breadcrumbs={[
          { name: "首页", url: "https://mogutv.com" },
          { name: article.title, url: canonicalUrl },
        ]}
      />

      <main className="pt-20">
        {/* Header */}
        <section className="w-full bg-background-100/70 border-b border-background-200/60">
          <div className="w-full px-4 md:px-6 py-8">
            <div className="mb-4">
              <Breadcrumbs
                items={[
                  { label: "首页", href: "/" },
                  { label: "评测分类", href: "/category/all" },
                  { label: article.title },
                ]}
              />
            </div>
            <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-3">
              {article.title}
            </h1>
            <div className="flex flex-wrap items-center gap-3 text-xs text-foreground-500">
              <span className="flex items-center gap-1">
                <i className="ri-user-line"></i> {article.author}
              </span>
              <span className="flex items-center gap-1">
                <i className="ri-calendar-line"></i> {article.publishDate}
              </span>
              <span className="flex items-center gap-1">
                <i className="ri-time-line"></i> {article.readTime}
              </span>
              <span className="flex items-center gap-1 text-primary-500">
                <i className="ri-star-fill"></i> {article.rating} 分
              </span>
            </div>
            <div className="flex flex-wrap items-center gap-2 mt-3">
              {article.tags.map((tag) => (
                <span key={tag} className="px-2.5 py-1 bg-secondary-100 text-secondary-800 text-xs rounded-md">
                  {tag}
                </span>
              ))}
            </div>
          </div>
        </section>

        {/* Content */}
        <section className="w-full bg-background-50 py-8 md:py-10">
          <div className="w-full px-4 md:px-6 max-w-4xl mx-auto">
            {/* Cover Image */}
            <div className="w-full h-64 md:h-96 rounded-lg overflow-hidden mb-8">
              <img
                src={article.coverImage}
                alt={article.title}
                className="w-full h-full object-cover"
                title={article.title}
              />
            </div>

            {/* Article Body */}
            <article
              className="prose-custom text-foreground-900"
              dangerouslySetInnerHTML={{ __html: article.content }}
            />

            {/* Rating Box */}
            <div className="mt-10 p-6 bg-background-100/80 rounded-lg border border-background-200/60 text-center">
              <p className="text-sm text-foreground-600 mb-2">综合评分</p>
              <div className="text-4xl font-heading font-bold text-primary-500 mb-1">
                {article.rating}
              </div>
              <div className="flex items-center justify-center gap-0.5">
                {Array.from({ length: 5 }).map((_, i) => (
                  <i
                    key={i}
                    className={`text-lg ${
                      i < Math.floor(article.rating)
                        ? "ri-star-fill text-yellow-400"
                        : "ri-star-line text-foreground-300"
                    }`}
                  ></i>
                ))}
              </div>
              <p className="text-xs text-foreground-500 mt-2">基于综合评测评分</p>
            </div>

            {/* Back Link */}
            <div className="mt-8 text-center">
              <Link
                to="/category/all"
                className="inline-flex items-center gap-2 px-5 py-2.5 bg-secondary-100 text-secondary-800 text-sm font-medium rounded-md hover:bg-secondary-200 transition-colors cursor-pointer"
              >
                <i className="ri-arrow-left-line"></i> 返回评测列表
              </Link>
            </div>
          </div>
        </section>

        {/* Related Articles */}
        {relatedArticles.length > 0 && (
          <section className="w-full bg-background-100/70 py-12 md:py-16">
            <div className="w-full px-4 md:px-6">
              <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mb-6 text-center">
                相关评测推荐
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 max-w-6xl mx-auto">
                {relatedArticles.map((ra) => (
                  <ArticleCard key={ra.id} article={ra} />
                ))}
              </div>
            </div>
          </section>
        )}
      </main>

      <style>{`
        .prose-custom h2 {
          font-size: 1.5rem;
          font-weight: 700;
          margin-top: 2rem;
          margin-bottom: 0.75rem;
          color: oklch(var(--foreground-950));
        }
        .prose-custom h3 {
          font-size: 1.2rem;
          font-weight: 600;
          margin-top: 1.5rem;
          margin-bottom: 0.5rem;
          color: oklch(var(--foreground-900));
        }
        .prose-custom p {
          font-size: 0.95rem;
          line-height: 1.85;
          margin-bottom: 1rem;
          color: oklch(var(--foreground-800));
        }
        .prose-custom strong {
          color: oklch(var(--primary-600));
          font-weight: 600;
        }
      `}</style>
    </>
  );
}